import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import './config/dayjs';
import './config/i18n/i18n';
import './index.css';
import App from './App.tsx';
import { WebSocketProvider } from './context/socketContext.tsx';
import { UserProvider } from './context/userContext.tsx';
import { LoadingProvider } from './context/loadingContext.tsx';
import { ThemeProvider } from './theme/ThemeProvider.tsx';
import { UnavailableServicePage } from './pages/UnavailableServicePage/UnavailableServicePage.tsx';
import { CssBaseline } from '@mui/material';
import { I18nLocaleProvider } from './providers/I18nLocaleProvider.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

Sentry.init({
  dsn: import.meta.env.VITE_APP_GOOGLE_MAP_API_KEY,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.browserTracingIntegration(),
  ],
  tracePropagationTargets: ['localhost', /^https:\/\/electric-group\.stream/],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <I18nLocaleProvider>
          <WebSocketProvider>
            <UserProvider>
              <LoadingProvider>
                  <ThemeProvider>
                    <Sentry.ErrorBoundary
                      fallback={
                        <>
                          <CssBaseline />
                          <UnavailableServicePage />
                        </>
                      }
                    >
                      <App />
                    </Sentry.ErrorBoundary>
                  </ThemeProvider>
              </LoadingProvider>
            </UserProvider>
          </WebSocketProvider>
        </I18nLocaleProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
